/**
 * Settings (`040-api-contract.md` → Settings, `100-settings.md`). **Reads only.**
 *
 * One document, not one REST resource per preference. `GET /api/settings` returns the whole
 * nested document; `PATCH /api/settings` deep-merges any subset and returns the FULL document.
 * The write lives in `features/settings/useSettings.ts` as an outbox entry, exactly like every
 * other mutation in the app (`112-offline-drafts.md` §5) — there is no `patchSettings()` here
 * and there must not be.
 *
 * ## The defaults below are a RENDERING fallback, not a second source of truth
 *
 * `100-settings.md` §2 is explicit: "a missing key falls back to its default… the fallback
 * lives in exactly one place, on the server", and `GET /api/settings` always returns a complete
 * document. That is true of what the server *sends*. It is not true of what the client *has*:
 * on the first paint of a cold boot the query has not resolved, and if the request fails the
 * screen still has to draw something. `resolveSettings()` exists for exactly those two moments.
 * It is never sent back — a PATCH carries only the keys the user actually touched.
 */
import { apiFetch } from '@/api/client';
import type { Language } from '@/lib/i18n';
import type { Density } from '@/stores/density';
import type { Theme } from '@/stores/theme';

/* ── The key table (`100-settings.md` §1) ───────────────────────────────────── */

/**
 * The model ids, exact and complete. **Never append a date suffix**
 * (`020-tech-stack.md`, and CLAUDE.md says it twice).
 */
export const AI_MODELS = [
  'claude-opus-4-8',
  'claude-sonnet-5',
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
] as const;
export type AiModel = (typeof AI_MODELS)[number];

/** `0` = Sunday … `6` = Saturday — the same 0–6 convention `date-fns` uses, stored raw. */
export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface AiSettings {
  model: AiModel;
  /** `1024 ≤ n ≤ 32768`. Bounds the OUTPUT. Not `contextTokenBudget`. */
  maxTokens: number;
  thinking: boolean;
  defaultSystemPromptId: string | null;
  /** `4096 ≤ n ≤ 180000`. Bounds the page context we ATTACH. Not `maxTokens`. */
  contextTokenBudget: number;
}

export interface UiSettings {
  theme: Theme;
  density: Density;
  /** `en` | `de`. The UI locale, and the language Claude is asked to answer in (`100` §1). */
  language: Language;
}

export interface CalendarSettings {
  weekStartsOn: WeekStartsOn;
  /** An IANA identifier, e.g. `Europe/Berlin`. Never a fixed offset — it could not express DST. */
  timezone: string;
}

/** What `GET`/`PATCH /api/settings` returns: always complete. */
export interface Settings {
  ai: AiSettings;
  ui: UiSettings;
  calendar: CalendarSettings;
}

/** What a PATCH carries: any subset, two levels deep. `{"ai": null}` is a 422, not a delete. */
export interface SettingsPatch {
  ai?: Partial<AiSettings>;
  ui?: Partial<UiSettings>;
  calendar?: Partial<CalendarSettings>;
}

/** What we may actually receive: a complete document, or nothing yet, or a failed fetch. */
export interface SettingsDto {
  ai?: Partial<AiSettings>;
  ui?: Partial<UiSettings>;
  calendar?: Partial<CalendarSettings>;
}

/* ── Bounds (`100-settings.md` §1) ──────────────────────────────────────────────
 * The client's copy of the bounds is a UX nicety and is never trusted: every write is
 * re-validated server-side against `SettingsRegistry`, and a 422 comes back with the dotted
 * key. These exist so the number inputs can say "1024–32768" and refuse obvious nonsense
 * before it costs a round-trip.
 */
export const MAX_TOKENS_MIN = 1024;
export const MAX_TOKENS_MAX = 32_768;
export const CONTEXT_BUDGET_MIN = 4_096;
export const CONTEXT_BUDGET_MAX = 180_000;

/** The seeded value that means "the user has not chosen a timezone yet" (`100` §1). */
export const TIMEZONE_SEED = 'UTC';

export const DEFAULT_WEEK_STARTS_ON: WeekStartsOn = 1; // Monday (`100-settings.md`)

export const SETTINGS_DEFAULTS: Settings = {
  ai: {
    model: 'claude-opus-4-8',
    maxTokens: 8192,
    thinking: false,
    defaultSystemPromptId: null,
    contextTokenBudget: 60_000,
  },
  ui: { theme: 'system', density: 'compact', language: 'en' },
  calendar: { weekStartsOn: DEFAULT_WEEK_STARTS_ON, timezone: TIMEZONE_SEED },
};

/* ── Reads ──────────────────────────────────────────────────────────────────── */

export function getSettings(): Promise<SettingsDto> {
  return apiFetch<SettingsDto>('/api/settings');
}

/**
 * `100-settings.md`: `calendar.timezone` has no static default — it is seeded to `UTC` and the
 * browser's detected zone is what the user actually wants on first run. Read as the FALLBACK
 * for a value the server has not given us, and once, at boot, as the value we PATCH in when the
 * stored one is still the untouched seed. The stored value always wins after that.
 */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || TIMEZONE_SEED;
  } catch {
    return TIMEZONE_SEED;
  }
}

/** Every IANA zone the browser knows, for the settings combobox. Falls back to the detected one. */
export function supportedTimezones(): readonly string[] {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [detectTimezone()];
  }
}

export function isWeekStartsOn(value: unknown): value is WeekStartsOn {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6;
}

function isAiModel(value: unknown): value is AiModel {
  return AI_MODELS.includes(value as AiModel);
}

function isTheme(value: unknown): value is Theme {
  return value === 'system' || value === 'light' || value === 'dark';
}

function isDensity(value: unknown): value is Density {
  return value === 'compact' || value === 'comfortable';
}

function isLanguage(value: unknown): value is Language {
  return value === 'en' || value === 'de';
}

/**
 * Overlay whatever the server gave us onto the defaults, discarding anything that does not
 * type-check. See the header: this is what the SCREEN renders from before the query resolves
 * and when it fails. It is not what we send back.
 */
export function resolveSettings(dto: SettingsDto | undefined): Settings {
  const ai = dto?.ai;
  const ui = dto?.ui;
  const calendar = dto?.calendar;

  return {
    ai: {
      model: isAiModel(ai?.model) ? ai.model : SETTINGS_DEFAULTS.ai.model,
      maxTokens: typeof ai?.maxTokens === 'number' ? ai.maxTokens : SETTINGS_DEFAULTS.ai.maxTokens,
      thinking: typeof ai?.thinking === 'boolean' ? ai.thinking : SETTINGS_DEFAULTS.ai.thinking,
      defaultSystemPromptId:
        typeof ai?.defaultSystemPromptId === 'string' ? ai.defaultSystemPromptId : null,
      contextTokenBudget:
        typeof ai?.contextTokenBudget === 'number'
          ? ai.contextTokenBudget
          : SETTINGS_DEFAULTS.ai.contextTokenBudget,
    },
    ui: {
      theme: isTheme(ui?.theme) ? ui.theme : SETTINGS_DEFAULTS.ui.theme,
      density: isDensity(ui?.density) ? ui.density : SETTINGS_DEFAULTS.ui.density,
      language: isLanguage(ui?.language) ? ui.language : SETTINGS_DEFAULTS.ui.language,
    },
    calendar: {
      weekStartsOn: isWeekStartsOn(calendar?.weekStartsOn)
        ? calendar.weekStartsOn
        : SETTINGS_DEFAULTS.calendar.weekStartsOn,
      timezone:
        typeof calendar?.timezone === 'string' && calendar.timezone !== ''
          ? calendar.timezone
          : SETTINGS_DEFAULTS.calendar.timezone,
    },
  };
}

/* ── Storage (`GET /api/storage`) ────────────────────────────────────────────
 *
 * `100-settings.md` §5 asks Settings → Data to show "Total `page_asset.size_bytes` and a page
 * count" and does not say where the figures come from; the backend answers with `/api/storage`.
 * Purely informational — nothing on the screen branches on it.
 */

export interface StorageDto {
  pageCount: number;
  trashedPageCount: number;
  assetCount: number;
  /** The sum of `page_asset.size_bytes`. Deduped: one file, counted once. */
  assetBytes: number;
}

export function getStorage(): Promise<StorageDto> {
  return apiFetch<StorageDto>('/api/storage');
}
